import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { apiClient } from '../lib/api-client';

type PublicStatus = {
  maintenance?: boolean;
  can_access_public?: boolean;
  message?: string;
};

export function RootLayout() {
  const location = useLocation();
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem('site-theme');
    return stored === 'dark' ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem('site-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));

  const { data: publicStatus } = useQuery<PublicStatus>({
    queryKey: ['public-status'],
    queryFn: async () => {
      const payload = (await apiClient.get('/public-status')).data;
      return payload?.data ?? payload;
    },
    staleTime: 15_000,
    retry: 0,
  });

  const isMaintenanceBlocked = Boolean(publicStatus?.maintenance) && !Boolean(publicStatus?.can_access_public);

  useEffect(() => {
    const key = 'site-anonymous-id';
    let anonymousId = localStorage.getItem(key);
    if (!anonymousId) {
      anonymousId = (window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).replace(/\s+/g, '');
      localStorage.setItem(key, anonymousId);
    }

    const pagePath = `${location.pathname}${location.search ?? ''}`;
    void apiClient.post('/tracking/page-view', {
      event_type: 'page_view',
      route: pagePath,
      anonymous_id: anonymousId,
      timestamp: new Date().toISOString(),
    }).catch(() => {
      // Tracking must never break navigation.
    });
  }, [location.pathname, location.search]);

  return (
    <div id="site-root" className={`flex min-h-screen flex-col ${theme === 'dark' ? 'site-dark' : 'site-light'}`}>
      <Header theme={theme} onToggleTheme={toggleTheme} />
      <main className="flex-1">
        {isMaintenanceBlocked ? (
          <div className="mx-auto flex min-h-[50vh] max-w-3xl items-center justify-center px-6 py-16 text-center">
            <div className="rounded-xl border bg-white/80 p-8 shadow-sm">
              <h1 className="text-2xl font-bold text-red-700">Site în mentenanță</h1>
              <p className="mt-3 text-sm text-gray-700">
                {publicStatus?.message || 'Rezerva Autocar este temporar indisponibil. Vă rugăm reveniți mai târziu.'}
              </p>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <Footer />
    </div>
  );
}
